import { RightArrowIcon } from "assets/icons";
import { If, Media } from "components";
import moment from "moment";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Icon, Modal, PersonInfo } from "ui";

import { useGetDamagedItem, useGetInventoryBoxQr } from "../../../apis";
import { InventoryProductVariant } from "../../InventoryProductVariant";
import { DamagedItemModalProps } from "../components.types";

import styles from "./DamagedItemModal.module.scss";

const DamagedItemModal = ({
  isOpen,
  onClose,
  damagedItemId
}: DamagedItemModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { damagedItem, isLoading } = useGetDamagedItem(damagedItemId);

  const goToProduct = () => {
    if (damagedItem?.productVariant) {
      const { categoryId, productId, id } = damagedItem.productVariant;
      navigate(`/products/details/${categoryId}/${productId}/${id}/boxes`);
    }
  };

  const { boxItem } = useGetInventoryBoxQr(damagedItem?.inventoryBoxId);

  return (
    <Modal
      isOpen={isOpen}
      title={t("Damaged item")}
      isLoading={isLoading}
      onClose={onClose}
      footer={false}
    >
      <div className={styles.damagedItemModal_wrapper}>
        <div className={styles.damagedItemModal_left}>
          {damagedItem?.productVariant && (
            <>
              <div className={styles.damagedItemModal_product}>
                <InventoryProductVariant
                  productVariant={damagedItem.productVariant}
                  type="lg"
                />
                <div
                  className={styles.damagedItemModal_productButton}
                  onClick={goToProduct}
                >
                  {t("Show original item")}
                  <Icon as={RightArrowIcon} boxSize={20} color="#FB3973" />
                </div>
              </div>
              <div className={styles.damagedItemModal_divider} />
            </>
          )}
          <div className={styles.damagedItemModal_info}>
            {damagedItem?.order?.id && (
              <DamagedItemModalInfoItem title={t("Order")}>
                {damagedItem.order?.id}
              </DamagedItemModalInfoItem>
            )}
            {damagedItem?.inventoryBoxId && boxItem && (
              <DamagedItemModalInfoItem title={t("Box")}>
                {boxItem.name}
              </DamagedItemModalInfoItem>
            )}
            <DamagedItemModalInfoItem title={t("Buying Price")}>
              {damagedItem?.productVariant.buyingPrice ?? "-"}
            </DamagedItemModalInfoItem>
            <DamagedItemModalInfoItem title={t("Date of return")}>
              {damagedItem?.damagedAt ? (
                <>
                  {`${moment(damagedItem.damagedAt).format("DD/MM/YYYY")}`}
                  {"\n"}
                  {`${moment(damagedItem.damagedAt).format("HH:mm")}`}
                </>
              ) : (
                "-"
              )}
            </DamagedItemModalInfoItem>

            {damagedItem?.order && (
              <DamagedItemModalInfoItem title={t("Reseller")}>
                <PersonInfo
                  dimensions={{ width: 32, height: 32, borderRadius: "100px" }}
                  image={damagedItem.order.reseller?.image}
                  personName={`${damagedItem.order.reseller?.firstname} ${damagedItem.order.reseller?.lastname}`}
                  nameClassName={styles.damagedItemModal_infoItem_managerName}
                />
              </DamagedItemModalInfoItem>
            )}
            {damagedItem?.order && (
              <DamagedItemModalInfoItem title={t("Location")}>
                {damagedItem.order.deliveryAddress}
              </DamagedItemModalInfoItem>
            )}
            <DamagedItemModalInfoItem title={t("Inventory Manager")}>
              {damagedItem?.inventoryManager ? (
                <PersonInfo
                  dimensions={{ width: 32, height: 32, borderRadius: "100px" }}
                  image={damagedItem.inventoryManager?.image}
                  personName={`${damagedItem.inventoryManager?.firstname} ${damagedItem.inventoryManager?.lastname}`}
                  nameClassName={styles.damagedItemModal_infoItem_managerName}
                />
              ) : (
                "-"
              )}
            </DamagedItemModalInfoItem>
          </div>
        </div>
        <If
          condition={damagedItem?.damageDescription ?? damagedItem?.damageImage}
        >
          <div className={styles.damagedItemModal_damageInfo}>
            <div className={styles.damagedItemModal_damageInfo_title}>
              {t("Manager’s note & picture")}
            </div>
            <div className={styles.damagedItemModal_damageInfo_description}>
              {damagedItem?.damageDescription}
            </div>
            {damagedItem?.damageImage && (
              <div className={styles.damagedItemModal_damageInfo_imageThumb}>
                {damagedItem?.damageImage && (
                  <Media
                    mediaFile={damagedItem?.damageImage}
                    className={styles.damagedItemModal_damageInfo_image}
                  />
                )}
              </div>
            )}
          </div>
        </If>
      </div>
    </Modal>
  );
};

export default DamagedItemModal;

const DamagedItemModalInfoItem = ({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className={styles.damagedItemModal_infoItem}>
    <div className={styles.damagedItemModal_infoItem_title}>{title}</div>
    <div className={styles.damagedItemModal_infoItem_content}>{children}</div>
  </div>
);
